import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import bgImage from "./assets/glassbackground2.jpg";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import { AuthProvider } from "./context/AuthContext";

const LoginPage    = React.lazy(() => import("./pages/LoginPage"));
const Dashboard    = React.lazy(() => import("./pages/Dashboard"));
const AdminPage    = React.lazy(() => import("./pages/AdminPage"));
const Profile      = React.lazy(() => import("./pages/Profile"));
const FinancialsPage  = React.lazy(() => import("./pages/FinancialsPage"));
const AnalyticsPage   = React.lazy(() => import("./pages/AnalyticsPage"));
const AIAssistantPage = React.lazy(() => import("./pages/AIAssistantPage"));
const NotAuthorized   = React.lazy(() => import("./pages/NotAuthorized"));
const NotFound        = React.lazy(() => import("./pages/NotFound"));
const BusinessSetupPage = React.lazy(() => import("./pages/BusinessSetupPage"));
const CreditorsPage   = React.lazy(() => import("./pages/CreditorsPage"));
const ExpensesPage    = React.lazy(() => import("./pages/ExpensesPage"));
const LubesPage       = React.lazy(() => import("./pages/LubesPage"));
const ReportsPage     = React.lazy(() => import("./pages/ReportsPage"));
const EmployeesPage         = React.lazy(() => import("./pages/EmployeesPage"));
const MaintenanceDashboard  = React.lazy(() => import("./pages/MaintenanceDashboard"));
const MaintenanceSetup      = React.lazy(() => import("./pages/MaintenanceSetup"));

function App() {
  return (
    <ErrorBoundary>
      <div style={{
        position: "fixed", inset: 0, zIndex: -1,
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        transform: "scale(1.08)",
        filter: "blur(8px) brightness(0.65) saturate(0.55)",
      }} />
      <Router>
        <AuthProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Login is the root entry point */}
            <Route path="/"      element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Protected dashboard routes */}
            <Route path="/dashboard"  element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/financials" element={<ProtectedRoute><FinancialsPage /></ProtectedRoute>} />
            <Route path="/creditors"  element={<ProtectedRoute><CreditorsPage /></ProtectedRoute>} />
            <Route path="/expenses"   element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
            <Route path="/lubes"      element={<ProtectedRoute><LubesPage /></ProtectedRoute>} />
            <Route path="/profile"    element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/reports"    element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/employees"  element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />

            {/* Admin-only */}
            <Route path="/admin" element={
              <RoleProtectedRoute allowedRoles={["admin", "superadmin"]}>
                <AdminPage />
              </RoleProtectedRoute>
            } />

            {/* Superadmin-only */}
            <Route path="/analytics" element={
              <RoleProtectedRoute allowedRoles={["superadmin"]}>
                <AnalyticsPage />
              </RoleProtectedRoute>
            } />
            <Route path="/ai-assistant" element={
              <RoleProtectedRoute allowedRoles={["admin", "superadmin"]}>
                <AIAssistantPage />
              </RoleProtectedRoute>
            } />
            <Route path="/business-setup" element={
              <RoleProtectedRoute allowedRoles={["superadmin"]}>
                <BusinessSetupPage />
              </RoleProtectedRoute>
            } />
            {/* Old setup links open the matching Business Setup tab. */}
            <Route path="/manage-creditors" element={<Navigate to="/business-setup?tab=creditors" replace />} />
            <Route path="/manage-lubes"     element={<Navigate to="/business-setup?tab=lubricants" replace />} />

            {/* Maintenance module */}
            <Route path="/maintenance" element={
              <RoleProtectedRoute allowedRoles={["admin","superadmin"]}>
                <MaintenanceDashboard />
              </RoleProtectedRoute>
            } />
            <Route path="/maintenance-setup" element={
              <RoleProtectedRoute allowedRoles={["superadmin"]}>
                <MaintenanceSetup />
              </RoleProtectedRoute>
            } />

            <Route path="/not-authorized" element={<NotAuthorized />} />
            <Route path="*"               element={<NotFound />} />
          </Routes>
        </Suspense>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
